import React, { useState } from 'react';
import { createUsers, bulkDeleteUsers } from '../services/adminApi';

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
        active
          ? 'text-teal-400 border-teal-400'
          : 'text-navy-400 border-transparent hover:text-white hover:border-navy-500'
      }`}
    >
      {children}
    </button>
  );
}

function ResultItem({ item, type }) {
  const isSuccess = item.success;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      isSuccess ? 'bg-teal-500/10' : 'bg-coral-500/10'
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
        isSuccess ? 'bg-teal-500/20 text-teal-400' : 'bg-coral-500/20 text-coral-400'
      }`}>
        {isSuccess ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-mono text-sm truncate ${isSuccess ? 'text-teal-300' : 'text-coral-300'}`}>
          {item.email || item.id}
        </p>
        {item.error && (
          <p className="text-xs text-coral-400 mt-0.5">{item.error}</p>
        )}
      </div>
    </div>
  );
}

export default function BulkOperations() {
  const [activeTab, setActiveTab] = useState('create');
  const [input, setInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const parseInput = () => {
    return input
      .split(/[\n,;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  };

  const handleCreateUsers = async () => {
    const emails = parseInput();
    if (emails.length === 0) {
      alert('Please enter at least one email address');
      return;
    }

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      alert(`Invalid email addresses:\n${invalidEmails.join('\n')}`);
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: emails.length });
    setResults(null);

    try {
      const result = await createUsers(emails);
      setResults({
        type: 'create',
        items: result.results || [],
        summary: {
          total: emails.length,
          success: result.results?.filter(r => r.success).length || 0,
          failed: result.results?.filter(r => !r.success).length || 0,
        },
      });
    } catch (err) {
      alert('Failed to create users: ' + err.message);
    } finally {
      setProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleDeleteUsers = async () => {
    const userIds = parseInput();
    if (userIds.length === 0) {
      alert('Please enter at least one user ID');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${userIds.length} user(s)? This action cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: userIds.length });
    setResults(null);

    try {
      const result = await bulkDeleteUsers(userIds);
      setResults({
        type: 'delete',
        items: result.results || [],
        summary: {
          total: userIds.length,
          success: result.results?.filter(r => r.success).length || 0,
          failed: result.results?.filter(r => !r.success).length || 0,
        },
      });
    } catch (err) {
      alert('Failed to delete users: ' + err.message);
    } finally {
      setProcessing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const itemCount = parseInput().length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Bulk Operations</h1>
        <p className="text-navy-300 mt-1">Create or delete multiple users at once</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-navy-700">
        <div className="flex gap-4">
          <TabButton active={activeTab === 'create'} onClick={() => setActiveTab('create')}>
            Create Users
          </TabButton>
          <TabButton active={activeTab === 'delete'} onClick={() => setActiveTab('delete')}>
            Delete Users
          </TabButton>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">
            {activeTab === 'create' ? 'Email Addresses' : 'User IDs'}
          </h3>
          <p className="text-navy-400 text-sm mb-4">
            {activeTab === 'create'
              ? 'Enter email addresses, one per line or separated by commas'
              : 'Enter user IDs, one per line or separated by commas'}
          </p>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={activeTab === 'create'
              ? 'user1@example.com\nuser2@example.com\nuser3@example.com'
              : 'user-id-1\nuser-id-2\nuser-id-3'}
            className="input-field font-mono text-sm h-64 resize-none"
            disabled={processing}
          />
          <div className="flex items-center justify-between mt-4">
            <span className="text-navy-400 text-sm">
              {itemCount} {activeTab === 'create' ? 'email(s)' : 'ID(s)'} detected
            </span>
            <button
              onClick={activeTab === 'create' ? handleCreateUsers : handleDeleteUsers}
              disabled={processing || itemCount === 0}
              className={`${activeTab === 'create' ? 'btn-primary' : 'btn-danger'} flex items-center gap-2`}
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : activeTab === 'create' ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Users
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Users
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">Results</h3>
          
          {processing && progress.total > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-navy-300 text-sm">Processing...</span>
                <span className="text-navy-400 text-sm">{progress.current} / {progress.total}</span>
              </div>
              <div className="h-2 bg-navy-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    activeTab === 'create' ? 'bg-teal-500' : 'bg-coral-500'
                  }`}
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {results ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-navy-800 rounded-lg text-center">
                  <p className="text-lg font-bold text-white">{results.summary.total}</p>
                  <p className="text-xs text-navy-400">Total</p>
                </div>
                <div className="p-3 bg-teal-500/10 rounded-lg text-center">
                  <p className="text-lg font-bold text-teal-400">{results.summary.success}</p>
                  <p className="text-xs text-teal-400">Success</p>
                </div>
                <div className="p-3 bg-coral-500/10 rounded-lg text-center">
                  <p className="text-lg font-bold text-coral-400">{results.summary.failed}</p>
                  <p className="text-xs text-coral-400">Failed</p>
                </div>
              </div>

              {/* Items */}
              <div className="max-h-80 overflow-y-auto space-y-2">
                {results.items.map((item, i) => (
                  <ResultItem key={i} item={item} type={results.type} />
                ))}
              </div>

              {/* Clear */}
              <button
                onClick={() => {
                  setResults(null);
                  setInput('');
                }}
                className="w-full btn-secondary"
              >
                Clear Results
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-navy-700 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-navy-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <p className="text-navy-300">Results will appear here</p>
              <p className="text-navy-500 text-sm mt-1">
                Enter {activeTab === 'create' ? 'email addresses' : 'user IDs'} and click the button to start
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">Instructions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-teal-400 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Creating Users
            </h4>
            <ul className="text-navy-400 text-sm space-y-1">
              <li>• Enter valid email addresses</li>
              <li>• Users will receive a welcome email with temporary password</li>
              <li>• Duplicate emails will be skipped</li>
              <li>• Invalid emails will show an error</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-coral-400 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Deleting Users
            </h4>
            <ul className="text-navy-400 text-sm space-y-1">
              <li>• Enter user IDs (from Cognito or user details page)</li>
              <li>• All user data will be permanently deleted</li>
              <li>• This includes documents, logs, and ShipBook data</li>
              <li>• This action cannot be undone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
